import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Typography, Space, message, Select, Input, Popconfirm, Spin } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Form } from 'react-bootstrap';
import {
  feedbackService,
  FeedbackPost,
  FeedbackComment,
  FeedbackTicket,
} from '../../services/feedback/feedbackService';
import { useAuth } from '../../contexts/AuthContext';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { getDemoFeedbackPostBundle, isDemoFeedbackPostId } from '../../demo/demoBrowseMocks';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';
import { feedbackCategoryPill, feedbackStatusPill } from './feedbackUi';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const FeedbackPostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role, isDemoBrowseOnly } = useAuth();
  const [post, setPost] = useState<FeedbackPost | null>(null);
  const [ticket, setTicket] = useState<FeedbackTicket | null>(null);
  const [comments, setComments] = useState<FeedbackComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusDraft, setStatusDraft] = useState<FeedbackPost['status'] | undefined>(undefined);
  const [progressDraft, setProgressDraft] = useState('');
  const [resolutionDraft, setResolutionDraft] = useState('');

  const isAdmin = role === '管理员' || role === '超级管理员';

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setCommentLoading(true);
    try {
      if (isDemoBrowseOnly && isDemoFeedbackPostId(id)) {
        const bundle = getDemoFeedbackPostBundle(id);
        if (!bundle) {
          message.error('未找到该演示帖子');
          navigate('/feedback/square');
          return;
        }
        setPost(bundle.post);
        setTicket(bundle.ticket);
        setComments(bundle.comments);
        setStatusDraft(bundle.post.status);
        setProgressDraft(bundle.ticket?.progressDetail || '');
        setResolutionDraft(bundle.ticket?.resolutionSummary || '');
        return;
      }
      const detail = await feedbackService.getPost(id);
      setPost(detail.post);
      setTicket(detail.ticket || null);
      const commentsResult = await feedbackService.listComments(id, { page: 1, pageSize: 100 });
      setComments(commentsResult.items);
      setStatusDraft(detail.post.status);
      setProgressDraft(detail.ticket?.progressDetail || '');
      setResolutionDraft(detail.ticket?.resolutionSummary || '');
    } catch (error: any) {
      message.error(error.message || '获取详情失败');
      navigate('/feedback/square');
    } finally {
      setLoading(false);
      setCommentLoading(false);
    }
  }, [id, navigate, isDemoBrowseOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleVote = async (type: 'like' | 'disagree') => {
    if (!post) return;
    try {
      if (isDemoBrowseOnly && isDemoFeedbackPostId(post._id)) {
        setPost((p) =>
          p
            ? {
                ...p,
                likeCount: type === 'like' ? p.likeCount + 1 : p.likeCount,
                disagreeCount: type === 'disagree' ? p.disagreeCount + 1 : p.disagreeCount
              }
            : p
        );
        message.info('演示模式：投票仅本地预览');
        return;
      }
      const updated = await feedbackService.votePost(post._id, type);
      setPost(updated);
    } catch (error: any) {
      message.error(error.message || '操作失败');
    }
  };

  const handleReport = async () => {
    if (!post) return;
    try {
      if (isDemoBrowseOnly && isDemoFeedbackPostId(post._id)) {
        message.info('演示模式：举报未提交服务器');
        return;
      }
      await feedbackService.reportPost(post._id, 'other', '');
      message.success('已提交举报，管理员会尽快处理');
    } catch (error: any) {
      message.error(error.message || '举报失败');
    }
  };

  const handleSubmitComment = async () => {
    if (!id || !commentText.trim()) return;
    setSubmittingComment(true);
    try {
      if (isDemoBrowseOnly && isDemoFeedbackPostId(id)) {
        const text = commentText.trim();
        setComments((prev) => [
          {
            _id: `demo-fbc-local-${Date.now()}`,
            post: id,
            author: 'me',
            parent: null,
            anonymousName: '我',
            content: text,
            isOfficial: false,
            likeCount: 0,
            reportCount: 0,
            status: 'visible',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          ...prev
        ]);
        setPost((p) => (p ? { ...p, commentCount: (p.commentCount || 0) + 1 } : p));
        setCommentText('');
        message.success('评论已展示（演示模式）');
        return;
      }
      const newComment = await feedbackService.createComment(id, commentText);
      setComments((prev) => [newComment, ...prev]);
      setCommentText('');
      message.success('评论成功');
    } catch (error: any) {
      message.error(error.message || '评论失败');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeletePost = async () => {
    if (!post) return;
    try {
      if (isDemoBrowseOnly && isDemoFeedbackPostId(post._id)) {
        message.info('演示模式：未删除服务器数据');
        navigate('/feedback/square');
        return;
      }
      await feedbackService.deletePost(post._id);
      message.success('帖子已删除');
      navigate('/feedback/square');
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const mapPostStatusToTicketStatus = (status: FeedbackPost['status']): FeedbackTicket['status'] => {
    if (status === 'processing') {
      return 'in_progress';
    }
    return status as FeedbackTicket['status'];
  };

  const handleUpdateStatus = async () => {
    if (!post || !statusDraft) return;
    setUpdatingStatus(true);
    try {
      if (isDemoBrowseOnly && isDemoFeedbackPostId(post._id)) {
        setPost((p) => (p ? { ...p, status: statusDraft } : p));
        setTicket((tk) =>
          tk
            ? {
                ...tk,
                status: mapPostStatusToTicketStatus(statusDraft),
                progressDetail: progressDraft,
                resolutionSummary: resolutionDraft
              }
            : tk
        );
        message.success('已更新（演示模式）');
        return;
      }
      const result = await feedbackService.updatePostStatus(post._id, {
        status: mapPostStatusToTicketStatus(statusDraft),
        progressDetail: progressDraft,
        resolutionSummary: resolutionDraft,
      });
      setPost(result.post);
      setTicket(result.ticket);
      message.success('状态与处理信息已更新');
    } catch (error: any) {
      message.error(error.message || '更新失败');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading || !post) {
    return (
      <div className="page-container full-height members-lims fb-lims d-flex align-items-center justify-content-center py-5">
        <Spin size="large" />
      </div>
    );
  }

  const titleShort = post.title.length > 36 ? `${post.title.slice(0, 36)}…` : post.title;

  return (
    <div className="page-container full-height members-lims fb-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <Link to="/feedback/square" className="fb-crumb-link">
          畅所欲言
        </Link>
        <span className="mm-crumb-sep">/</span>
        <span aria-current="page">{titleShort}</span>
      </nav>

      <header className="mm-page-head">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 w-100">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              {feedbackCategoryPill(post.category)}
              {feedbackStatusPill(post.status)}
              <Text type="secondary" className="small mb-0">
                匿名 · {new Date(post.createdAt).toLocaleString('zh-CN')}
              </Text>
            </div>
            <h1 className="mm-title mb-0" style={{ wordBreak: 'break-word' }}>
              {post.title}
            </h1>
          </div>
          <div className="fb-detail-toolbar d-flex flex-wrap gap-2 justify-content-end">
            <button type="button" className="fb-btn fb-btn--ghost" onClick={() => navigate(-1)}>
              <LimsNavIcon name="arrow_back" sizePx={18} aria-hidden />
              返回
            </button>
            <button type="button" className="fb-btn fb-btn--primary-outline" onClick={() => handleVote('like')}>
              <LimsNavIcon name="thumb_up" sizePx={18} aria-hidden />
              赞同 ({post.likeCount})
            </button>
            <button type="button" className="fb-btn fb-btn--primary-outline" onClick={() => handleVote('disagree')}>
              <LimsNavIcon name="thumb_down" sizePx={18} aria-hidden />
              不认同 ({post.disagreeCount})
            </button>
            <Popconfirm title="确定要举报这条内容吗？" okText="举报" cancelText="取消" onConfirm={handleReport}>
              <button type="button" className="fb-btn fb-btn--warn-outline">
                <LimsNavIcon name="flag" sizePx={18} aria-hidden />
                举报
              </button>
            </Popconfirm>
            <Popconfirm title="确定删除该反馈？" okText="删除" cancelText="取消" onConfirm={handleDeletePost}>
              <button type="button" className="fb-btn fb-btn--danger-outline">
                <LimsNavIcon name="delete_outline" sizePx={18} aria-hidden />
                删除
              </button>
            </Popconfirm>
          </div>
        </div>
      </header>

      <div className="fb-detail-panel">
        {post.media && post.media.length > 0 && (
          <>
            <h2 className="fb-section-title" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
              相关图片 / 视频
            </h2>
            <Space wrap>
              {post.media.map((m) =>
                m.type === 'image' ? (
                  <img
                    key={m.key}
                    src={m.url}
                    alt={m.fileName}
                    style={{ maxWidth: 200, maxHeight: 150, objectFit: 'cover', borderRadius: 8 }}
                  />
                ) : (
                  <video key={m.key} src={m.url} controls style={{ maxWidth: 260, maxHeight: 180, borderRadius: 8 }} />
                ),
              )}
            </Space>
          </>
        )}

        <h2 className="fb-section-title">详细描述</h2>
        <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{post.content}</Paragraph>

        {isAdmin && (
          <>
            <h2 className="fb-section-title">管理员处理面板</h2>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space wrap>
                <Text>当前状态：</Text>
                {feedbackStatusPill(post.status)}
              </Space>
              <div>
                <Text className="d-block mb-2">设置状态</Text>
                <Select
                  style={{ width: '100%', maxWidth: 280 }}
                  value={statusDraft}
                  onChange={(v: FeedbackPost['status']) => setStatusDraft(v)}
                >
                  <Option value="pending">待处理</Option>
                  <Option value="accepted">已受理</Option>
                  <Option value="processing">处理中</Option>
                  <Option value="resolved">已解决</Option>
                  <Option value="rejected">不予受理</Option>
                </Select>
              </div>
              <div>
                <Text className="d-block mb-2">处理进度</Text>
                <TextArea
                  rows={3}
                  value={progressDraft}
                  onChange={(e) => setProgressDraft(e.target.value)}
                  placeholder="采取的措施、跟进情况等（可选）"
                />
              </div>
              <div>
                <Text className="d-block mb-2">处理结果</Text>
                <TextArea
                  rows={3}
                  value={resolutionDraft}
                  onChange={(e) => setResolutionDraft(e.target.value)}
                  placeholder="已解决或不予受理时建议说明原因"
                />
              </div>
              <button
                type="button"
                className="mm-btn-primary"
                disabled={updatingStatus}
                onClick={() => void handleUpdateStatus()}
              >
                {updatingStatus ? '保存中…' : '保存处理状态'}
              </button>
            </Space>
          </>
        )}

        {ticket && (
          <>
            <h2 className="fb-section-title">官方处理进度</h2>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>当前状态：{feedbackStatusPill(post.status)}</Text>
              {ticket.assignee && <Text>责任人：{ticket.assignee}</Text>}
              {ticket.progressDetail && <Paragraph className="mb-1">处理进度：{ticket.progressDetail}</Paragraph>}
              {ticket.resolutionSummary && <Paragraph className="mb-0">处理结果：{ticket.resolutionSummary}</Paragraph>}
            </Space>
          </>
        )}

        <h2 className="fb-section-title">评论（{comments.length}）</h2>
        {post.allowComment ? (
          <>
            <Form.Control
              as="textarea"
              rows={3}
              className="mb-2"
              placeholder={post.adminOnlyReply ? '该反馈仅允许管理员回复' : '写下你的想法（匿名显示）'}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={post.adminOnlyReply && !isAdmin}
            />
            <button
              type="button"
              className="mm-btn-primary d-inline-flex align-items-center gap-2"
              disabled={(post.adminOnlyReply && !isAdmin) || submittingComment || !commentText.trim()}
              onClick={() => void handleSubmitComment()}
            >
              <LimsNavIcon name="send" sizePx={18} aria-hidden />
              发表评论
            </button>
          </>
        ) : (
          <Text type="secondary">该反馈已关闭评论。</Text>
        )}

        {commentLoading ? (
          <div className="text-muted small py-3">加载评论…</div>
        ) : (
          <ul className="kb-comment-list mt-3">
            {comments.map((c) => (
              <li key={c._id} className="kb-comment-item">
                <div className="kb-comment-avatar" aria-hidden>
                  {c.isOfficial ? '官' : c.anonymousName.charAt(0)}
                </div>
                <div>
                  <div className="kb-comment-head">
                    <span className="fw-semibold">{c.isOfficial ? '官方回复' : c.anonymousName}</span>
                    <span className="text-muted small ms-2">{new Date(c.createdAt).toLocaleString('zh-CN')}</span>
                  </div>
                  <p className="kb-comment-body mb-0">{c.content}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default FeedbackPostDetail;
